import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { rupiah, num, pct } from "@/lib/format";
import StatCard from "@/components/StatCard";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Boxes, Calculator, Ruler, Leaf, Cloud, Trash2, Tag, Gauge, BrainCircuit, AlertTriangle, SlidersHorizontal, TrendingUp, TrendingDown,
} from "lucide-react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

const COLORS = ["#15803d", "#0ea5e9", "#f59e0b", "#ef4444", "#1e293b"];
const STATUS_VARIANT = {
  selesai: "bg-green-100 text-green-800 border-green-200",
  berjalan: "bg-blue-100 text-blue-800 border-blue-200",
  rencana: "bg-amber-100 text-amber-800 border-amber-200",
  batal: "bg-red-100 text-red-800 border-red-200",
};

function ChartCard({ title, children, height = 260 }) {
  return (
    <Card className="p-5 border-slate-200 shadow-sm">
      <h3 className="font-heading font-semibold text-slate-900 mb-4">{title}</h3>
      <ResponsiveContainer width="100%" height={height}>{children}</ResponsiveContainer>
    </Card>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [semenPct, setSemenPct] = useState(10);
  const [bbmPct, setBbmPct] = useState(10);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-slate-400 py-20 text-center">Memuat dashboard...</div>;
  const s = data.stats;
  const sv = data.sensitivity || { green_hpp: 0, semen_cost: 0, bbm_cost: 0, total_volume: 0, hpp_per_m3: 0 };
  const deltaSemen = sv.semen_cost * (Number(semenPct) || 0) / 100;
  const deltaBbm = sv.bbm_cost * (Number(bbmPct) || 0) / 100;
  const newHpp = sv.green_hpp + deltaSemen + deltaBbm;
  const newPerM3 = sv.total_volume ? newHpp / sv.total_volume : 0;
  const deltaHpp = newHpp - sv.green_hpp;
  const deltaPct = sv.green_hpp ? (deltaHpp / sv.green_hpp) * 100 : 0;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-bold text-slate-900">Dashboard ESG & Green HPP</h2>
        <p className="text-sm text-slate-500 mt-1">Ringkasan biaya produksi, lingkungan, dan emisi karbon.</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Boxes} label="Total Batch" value={num(s.total_batch)} sub="Batch produksi" accent="slate" testid="stat-total-batch" />
        <StatCard icon={Calculator} label="Total Green HPP" value={rupiah(s.total_green_hpp)} sub="Akumulasi semua batch" accent="green" testid="stat-green-hpp" />
        <StatCard icon={Ruler} label="HPP per m³" value={rupiah(s.hpp_per_m3)} sub="Rata-rata per m³" accent="green" testid="stat-hpp-unit" />
        <StatCard icon={Leaf} label="Total Biaya Lingkungan" value={rupiah(s.total_env_cost)} accent="green" testid="stat-env-cost" />
        <StatCard icon={Cloud} label="Total Emisi CO₂" value={`${num(s.total_co2_kg, 1)} kg`} sub={`${num(s.total_co2_ton, 2)} ton CO₂`} accent="red" testid="stat-co2" />
        <StatCard icon={Trash2} label="Waste Rate" value={pct(s.waste_rate)} sub="Rata-rata batch" accent="amber" testid="stat-waste" />
        <StatCard icon={Tag} label="Harga Jual Rekomendasi" value={rupiah(s.recommended_price)} sub="Rata-rata per unit" accent="blue" testid="stat-price" />
        <StatCard icon={Gauge} label="AI Efficiency Score" value={`${s.ai_score}/100`} sub="Skor efisiensi" accent="green" testid="stat-ai-score" />
      </div>

      {/* Sensitivitas Harga */}
      <Card className="p-5 border-slate-200 shadow-sm" data-testid="sensitivity-panel">
        <div className="flex items-center gap-2 mb-1">
          <SlidersHorizontal className="h-5 w-5 text-green-700" />
          <h3 className="font-heading font-semibold text-slate-900">Sensitivitas Harga → Dampak ke HPP</h3>
        </div>
        <p className="text-sm text-slate-500 mb-5">Simulasikan kenaikan harga Semen dan BBM untuk melihat dampaknya langsung terhadap Green HPP.</p>
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="space-y-5">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-slate-600">Kenaikan Harga Semen</label>
                <span className="text-sm font-bold text-green-700 tabular-nums" data-testid="sens-semen-val">+{num(semenPct)}%</span>
              </div>
              <input type="range" min="0" max="50" step="1" value={semenPct} onChange={(e) => setSemenPct(Number(e.target.value))}
                className="w-full accent-green-700 cursor-pointer" data-testid="sens-semen-slider" />
              <p className="text-[11px] text-slate-400 mt-1">Basis biaya semen: {rupiah(sv.semen_cost)}</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-slate-600">Kenaikan Harga BBM (Solar)</label>
                <span className="text-sm font-bold text-green-700 tabular-nums" data-testid="sens-bbm-val">+{num(bbmPct)}%</span>
              </div>
              <input type="range" min="0" max="50" step="1" value={bbmPct} onChange={(e) => setBbmPct(Number(e.target.value))}
                className="w-full accent-green-700 cursor-pointer" data-testid="sens-bbm-slider" />
              <p className="text-[11px] text-slate-400 mt-1">Basis biaya BBM: {rupiah(sv.bbm_cost)}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Green HPP Saat Ini</p>
              <p className="font-heading font-bold text-lg mt-1 text-slate-900" data-testid="sens-base-hpp">{rupiah(sv.green_hpp)}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">{rupiah(sv.hpp_per_m3)}/m³</p>
            </div>
            <div className="rounded-lg border border-green-200 bg-green-50 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-green-700">Green HPP Setelah Simulasi</p>
              <p className="font-heading font-bold text-lg mt-1 text-green-700" data-testid="sens-new-hpp">{rupiah(newHpp)}</p>
              <p className="text-[11px] text-green-600 mt-0.5">{rupiah(newPerM3)}/m³</p>
            </div>
            <div className="col-span-2 rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {deltaHpp >= 0 ? <TrendingUp className="h-5 w-5 text-amber-600" /> : <TrendingDown className="h-5 w-5 text-green-600" />}
                <p className="text-sm text-amber-800">Perubahan HPP</p>
              </div>
              <div className="text-right">
                <p className={`font-heading font-bold text-lg ${deltaHpp >= 0 ? "text-amber-700" : "text-green-700"}`} data-testid="sens-delta">
                  {deltaHpp >= 0 ? "+" : "-"}{rupiah(Math.abs(deltaHpp))}
                </p>
                <p className="text-[11px] text-amber-600">{deltaHpp >= 0 ? "+" : "-"}{num(Math.abs(deltaPct), 2)}% dari HPP</p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Charts row 1 */}
      <div className="grid lg:grid-cols-2 gap-6">
        <ChartCard title="Komposisi Biaya (BBB · BTKL · BOP · Lingkungan)">
          <PieChart>
            <Pie data={data.cost_composition} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => e.name}>
              {data.cost_composition.map((entry, i) => <Cell key={entry.name} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip formatter={(v) => rupiah(v)} />
          </PieChart>
        </ChartCard>
        <ChartCard title="Tren HPP per Batch">
          <LineChart data={data.hpp_trend}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1e6).toFixed(0)}jt`} />
            <Tooltip formatter={(v) => rupiah(v)} />
            <Legend />
            <Line type="monotone" dataKey="hpp" name="Green HPP" stroke="#15803d" strokeWidth={2.5} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="tradisional" name="HPP Tradisional" stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 5" dot={false} />
          </LineChart>
        </ChartCard>
      </div>

      {/* Charts row 2 */}
      <div className="grid lg:grid-cols-3 gap-6">
        <ChartCard title="Emisi Karbon per Sumber">
          <BarChart data={data.emisi_sources}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => `${num(v, 1)} kg CO₂`} />
            <Bar dataKey="value" name="kg CO₂" fill="#ef4444" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ChartCard>
        <ChartCard title="Waste Material per Batch">
          <BarChart data={data.waste_chart}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => `${num(v, 2)}%`} />
            <Bar dataKey="waste_rate" name="Waste %" fill="#f59e0b" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ChartCard>
        <ChartCard title="Biaya Lingkungan">
          <BarChart data={data.env_chart} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1e6).toFixed(0)}jt`} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={90} />
            <Tooltip formatter={(v) => rupiah(v)} />
            <Bar dataKey="value" name="Biaya" fill="#15803d" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ChartCard>
      </div>

      {/* Tables + AI */}
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-5 border-slate-200 shadow-sm">
          <h3 className="font-heading font-semibold text-slate-900 mb-4">Batch Produksi Terbaru</h3>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead>No. Batch</TableHead><TableHead>Produk</TableHead>
                  <TableHead className="text-right">Volume</TableHead>
                  <TableHead className="text-right">Green HPP</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.recent_batches.map((b) => (
                  <TableRow key={b.id} className="hover:bg-slate-50">
                    <TableCell className="font-medium">{b.no_batch}</TableCell>
                    <TableCell>{b.product_name}</TableCell>
                    <TableCell className="text-right tabular-nums">{num(b.volume, 1)} {b.satuan}</TableCell>
                    <TableCell className="text-right tabular-nums">{rupiah(b.green_hpp)}</TableCell>
                    <TableCell><Badge className={`${STATUS_VARIANT[b.status]} border font-medium capitalize`}>{b.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>

        <div className="space-y-6">
          <Card className="p-5 border-slate-200 shadow-sm bg-gradient-to-br from-green-50 to-white">
            <div className="flex items-center gap-2 mb-3">
              <BrainCircuit className="h-5 w-5 text-green-700" />
              <h3 className="font-heading font-semibold text-slate-900">AI Insight</h3>
            </div>
            <p className="text-sm text-slate-600">
              Skor efisiensi <b className="text-green-700">{s.ai_score}/100</b>. Waste rate rata-rata {pct(s.waste_rate)}.
            </p>
            <Link to="/app/ai" className="text-sm text-green-700 font-medium hover:underline inline-block mt-3" data-testid="dash-ai-link">
              Lihat analisis lengkap →
            </Link>
          </Card>

          <Card className="p-5 border-slate-200 shadow-sm">
            <h3 className="font-heading font-semibold text-slate-900 mb-3">Cost Driver Tertinggi</h3>
            <div className="space-y-2.5">
              {data.cost_drivers.map((d) => (
                <div key={d.name}>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">{d.name}</span>
                    <span className="font-medium text-slate-900">{d.pct}%</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full mt-1 overflow-hidden">
                    <div className="h-full bg-green-600 rounded-full" style={{ width: `${d.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {/* Alerts */}
      <Card className="p-5 border-slate-200 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          <h3 className="font-heading font-semibold text-slate-900">Alert & Anomali Biaya</h3>
        </div>
        {data.alerts.length === 0 ? (
          <p className="text-sm text-slate-400">Tidak ada anomali terdeteksi.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {data.alerts.map((a, i) => (
              <div key={`${a.no_batch}-${a.material}-${i}`} className="flex items-start gap-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm text-amber-900">
                  <b>{a.no_batch}</b> — Waste {a.material} {pct(a.waste_pct)} melebihi standar {pct(a.standar)}.
                </p>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
